import { bitmapContentSize } from '@hibro/shared';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { CaptureEditor } from './capture-editor';
import { captureHostWindow, logCapture } from './capture-host';

interface CaptureFrame {
  png: Uint8Array;
  width: number;
  height: number;
  scaleFactor: number;
}

/** Desktop capture window (`?capture=<displayId>`). No workbench, no host. */
export function mountCaptureWindow(
  container: HTMLElement,
  _displayId: string,
): void {
  const root = createRoot(container);
  root.render(<CaptureWindow />);
}

function CaptureWindow() {
  const [frame, setFrame] = useState<CaptureFrame | null>(null);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const capture = captureHostWindow().hibroCapture;
    if (!capture?.onFrame || !capture.onReset || !capture.ready) {
      logCapture('capture frame bridge is missing');
      return;
    }
    const offFrame = capture.onFrame((value) => {
      if (
        !value ||
        value.png.byteLength === 0 ||
        value.width <= 0 ||
        value.height <= 0 ||
        !(value.scaleFactor > 0)
      ) {
        void capture.fail('capture frame missing');
        return;
      }
      try {
        bitmapContentSize(value.width, value.height, value.scaleFactor);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'capture frame failed to size';
        logCapture(message);
        void capture.fail(message);
        return;
      }
      setFrame(value);
      setGeneration((current) => current + 1);
    });
    const offReset = capture.onReset(() => {
      setFrame(null);
    });
    void capture.ready();
    return () => {
      offFrame();
      offReset();
    };
  }, []);
  if (!frame) {
    return <div className="fixed inset-0 bg-black" />;
  }
  return (
    <CaptureEditor
      key={generation}
      png={frame.png}
      imageWidth={frame.width}
      imageHeight={frame.height}
      scaleFactor={frame.scaleFactor}
      onPainted={() => {
        void captureHostWindow().hibroCapture?.painted();
      }}
      onPaintFailed={() => {
        logCapture('capture frame failed to decode');
        void captureHostWindow().hibroCapture?.fail(
          'capture frame failed to decode',
        );
      }}
      onConfirm={(png) => {
        void captureHostWindow().hibroCapture?.finish({
          action: 'confirm',
          png,
        });
      }}
      onCopy={async (png) => {
        const host = captureHostWindow();
        try {
          const ok = (await host.hibroClipboard?.writeImage(png)) === true;
          if (!ok) {
            logCapture('capture copy failed');
            return false;
          }
          await host.hibroCapture?.finish({ action: 'copy' });
          return true;
        } catch (error) {
          logCapture('capture copy failed', error);
          return false;
        }
      }}
      onCancel={() => {
        void captureHostWindow().hibroCapture?.finish({ action: 'cancel' });
      }}
    />
  );
}
